import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Star, Image as ImageIcon, FolderOpen, Package, Pencil, Trash2, Plus, Heart, Eye, BarChart3, ToggleLeft, ToggleRight } from "lucide-react";
import { getGalleriesByEvent, updateEventGallery, archiveEventGallery, setShareGalleryActive } from "../../services/galleryService";
import { getEventById } from "../../services/eventService";
import { getRoomAccess } from "../../services/roomService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import Badge from "../../components/common/Badge";
import EmptyState from "../../components/common/EmptyState";
import CreateGalleryModal from "../../components/common/CreateGalleryModal";
import ConfirmModal from "../../components/common/ConfirmModal";
import EditNameModal from "../../components/common/EditNameModal";
import RoomHomeHeader from "../../components/rooms/RoomHomeHeader";
import SupportBannerModal from "../../components/rooms/SupportBannerModal";
import ShareGalleryConsoleModal from "../../components/event-details/ShareGalleryConsoleModal";
import { toast } from "react-toastify";
import { color, radius, shadow } from "../../styles/theme";

// Gallery lifecycle types that don't behave like a normal user-manageable
// gallery: the Social Album (IsUserGallery) can't be deleted/deactivated at
// all, the Thank You Banner (IsFundRaising) only supports activate/deactivate
// via its own banner manager, and the Share Gallery only supports
// activate/deactivate too (see MediaTab.jsx, the Event-side equivalent of
// this page, for the same rules).
const isSocialAlbum = (gallery) => gallery?.IsUserGallery === true;
const isFundraisingGallery = (gallery) => gallery?.IsFundRaising === true;
const isShareGallery = (gallery) => gallery?.GalleryType === 3 || gallery?.GalleryType === "ShareGallery";

const RoomGalleries = () => {
  const { t } = useTranslation(["rooms", "common"]);
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [room, setRoom] = useState(state?.room);
  const [isPrivateARoom, setIsPrivateARoom] = useState(false);

  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateGallery, setShowCreateGallery] = useState(false);
  const [editGalleryTarget, setEditGalleryTarget] = useState(null);
  const [deleteGalleryTarget, setDeleteGalleryTarget] = useState(null);
  const [editGalleryName, setEditGalleryName] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [shareRankingTarget, setShareRankingTarget] = useState(null);
  const [shareActiveTarget, setShareActiveTarget] = useState(null);
  const [shareActiveLoading, setShareActiveLoading] = useState(false);

  const eventId = room?.EventId || room?.Id || roomId;

  useEffect(() => {
    getGalleriesByEvent(eventId)
      .then((res) => setGalleries(res.data?.Result || []))
      .catch(() => toast.error(t("roomGalleries.toasts.loadFailed")))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    getRoomAccess(eventId)
      .then((res) => setIsPrivateARoom(Number(res.data?.AccessMode) !== 1))
      .catch(() => setIsPrivateARoom(false));
  }, [eventId]);

  // The description shown/edited here must come from the Events domain, not
  // from the chat-room-list object (`Chat/GetMyChatRooms`, the only source of
  // `room` otherwise) — that's the one place the mobile app's own @Room
  // description edit actually writes to, and the mismatch was why an
  // app-added description never appeared on the Panel.
  useEffect(() => {
    if (!eventId) return;
    getEventById(eventId)
      .then((res) => {
        const ev = res.data?.Result || res.data;
        if (ev) setRoom((prev) => (prev ? { ...prev, Description: ev.Description } : prev));
      })
      .catch(() => {});
  }, [eventId]);

  const refreshGalleries = () => {
    getGalleriesByEvent(eventId)
      .then((res) => setGalleries(res.data?.Result || []))
      .catch(() => toast.error(t("roomGalleries.toasts.loadFailed")));
  };

  const handleGalleryClick = (gallery) => {
    if (isFundraisingGallery(gallery)) {
      // The Thank You Banner is a single-image activate/deactivate toggle,
      // not a real album — it must never open the normal Albums page.
      setShowBannerModal(true);
      return;
    }
    navigate(`/rooms/${roomId}/gallery/${gallery.Id}`, {
      state: { gallery, backPath: `/rooms/${roomId}/galleries`, room },
    });
  };

  const confirmToggleShareActive = async () => {
    if (!shareActiveTarget) return;
    setShareActiveLoading(true);
    try {
      await setShareGalleryActive(shareActiveTarget.Id, eventId, !shareActiveTarget.ShareBannerActive);
      toast.success(shareActiveTarget.ShareBannerActive ? t("roomGalleries.toasts.shareDeactivated") : t("roomGalleries.toasts.shareActivated"));
      setShareActiveTarget(null);
      refreshGalleries();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("roomGalleries.toasts.shareToggleFailed"));
    } finally {
      setShareActiveLoading(false);
    }
  };

  const openEditGalleryModal = (gallery) => {
    setEditGalleryTarget(gallery);
    setEditGalleryName(gallery?.Name || "");
  };

  const handleEditGallery = async () => {
    const trimmed = editGalleryName.trim();
    if (!trimmed) {
      toast.warning(t("roomGalleries.toasts.nameRequired"));
      return;
    }

    setActionLoading(true);
    try {
      await updateEventGallery(editGalleryTarget.Id, { EventId: Number(eventId), Name: trimmed });
      toast.success(t("roomGalleries.toasts.updated"));
      setEditGalleryTarget(null);
      refreshGalleries();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("roomGalleries.toasts.updateFailed"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteGallery = async () => {
    if (!deleteGalleryTarget) return;

    setActionLoading(true);
    try {
      await archiveEventGallery({ galleryId: deleteGalleryTarget.Id, eventId, name: deleteGalleryTarget.Name });
      toast.success(t("roomGalleries.toasts.deleted"));
      setDeleteGalleryTarget(null);
      refreshGalleries();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("roomGalleries.toasts.deleteFailed"));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div>
      {room && (
        <RoomHomeHeader
          room={room}
          eventId={eventId}
          onUpdated={(newDescription) => setRoom((prev) => ({ ...prev, Description: newDescription }))}
        />
      )}

      <div style={s.sectionHeader}>
        <h3 style={s.sectionTitle}>{t("roomGalleries.sectionTitle")}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!loading && <span style={s.sectionCount}>{t("roomGalleries.countFound", { count: galleries.length })}</span>}
          <Button size="sm" icon={Plus} onClick={() => setShowCreateGallery(true)}>{t("roomGalleries.newGalleryButton")}</Button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : galleries.length === 0 ? (
        <EmptyState icon={ImageIcon} text={t("roomGalleries.emptyText")} />
      ) : (
        <div style={s.galleryGrid}>
          {galleries.map((gallery) => {
            const socialAlbum = isSocialAlbum(gallery);
            const fundraisingGallery = isFundraisingGallery(gallery);
            const shareGallery = isShareGallery(gallery);
            // Delete and rename are both off-limits for the Social Album (it's
            // participant/Event Host content, not the Promoter's), and for the
            // Thank You Banner and Share Gallery (their name/lifecycle is
            // fixed, only activate/deactivate applies).
            const canDelete = !socialAlbum && !fundraisingGallery && !shareGallery;
            const canEdit = !socialAlbum && !fundraisingGallery && !shareGallery;
            return (
              <div key={gallery.Id} className="icon-tile" style={s.galleryCard} onClick={() => handleGalleryClick(gallery)}>
                <div style={{ ...s.colorBar, background: gallery.IsPremium ? color.warning500 : color.primary500 }} />
                <div style={s.galleryBody}>
                  <div style={s.galleryTopRow}>
                    <span style={s.galleryName} title={gallery.Name}>{gallery.Name}</span>
                    <div style={s.galleryActions}>
                      {gallery.IsPremium && <Badge variant="warning"><Star size={9} strokeWidth={2.5} /> {t("roomGalleries.premiumBadge")}</Badge>}
                      {canEdit && (
                        <button style={s.cardActionBtn} onClick={(e) => { e.stopPropagation(); openEditGalleryModal(gallery); }} title={t("roomGalleries.editGalleryTitle")}>
                          <Pencil size={12} strokeWidth={2.25} />
                        </button>
                      )}
                      {canDelete && (
                        <button style={{ ...s.cardActionBtn, ...s.deleteActionBtn }} onClick={(e) => { e.stopPropagation(); setDeleteGalleryTarget(gallery); }} title={t("roomGalleries.deleteGalleryTitle")}>
                          <Trash2 size={12} strokeWidth={2.25} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={s.galleryCounts}>
                    <span style={s.countItem}><ImageIcon size={12} strokeWidth={2} /> {t("roomGalleries.mediaCount", { count: gallery.MediaCount })}</span>
                    <span style={s.countItem}><FolderOpen size={12} strokeWidth={2} /> {t("roomGalleries.albumCount", { count: gallery.AlbumCount })}</span>
                  </div>
                  {gallery.Package && <div style={s.packageName}><Package size={11} strokeWidth={2} /> {gallery.Package.Name}</div>}
                  <div style={s.galleryMeta}>
                    {gallery.IsWatermark && <span style={s.tag}>{t("roomGalleries.tags.watermark")}</span>}
                    {gallery.IsMediaHandlesGallery && <span style={s.tag}>{t("roomGalleries.tags.mediaHandles")}</span>}
                    {gallery.IsUserGallery && <span style={s.tag}>{t("roomGalleries.tags.userGallery")}</span>}
                    {fundraisingGallery && <span style={s.tag}>{t("roomGalleries.tags.thankYouBanner")}</span>}
                    {fundraisingGallery && (
                      <Badge variant={gallery.IsFundRaisingActive ? "success" : "neutral"}>
                        {gallery.IsFundRaisingActive ? t("roomGalleries.activeStatus") : t("roomGalleries.deactivatedStatus")}
                      </Badge>
                    )}
                  </div>
                  {fundraisingGallery && (
                    <div style={s.shareGalleryActions}>
                      <button type="button" className="btn btn-ghost btn-sm" style={s.shareActionButton} onClick={(e) => { e.stopPropagation(); setShowBannerModal(true); }}>
                        <Heart size={13} /> {t("roomGalleries.manageBanner")}
                      </button>
                    </div>
                  )}
                  {shareGallery && (
                    <div style={s.shareGalleryActions}>
                      <button type="button" className="btn btn-ghost btn-sm" style={s.shareActionButton} onClick={(e) => { e.stopPropagation(); handleGalleryClick(gallery); }}>
                        <Eye size={13} /> {t("roomGalleries.viewAlbums")}
                      </button>
                      <button type="button" className="btn btn-secondary btn-sm" style={s.shareActionButton} onClick={(e) => { e.stopPropagation(); setShareRankingTarget(gallery); }}>
                        <BarChart3 size={13} /> {t("roomGalleries.rankingSettlement")}
                      </button>
                      <button type="button" className="btn btn-secondary btn-sm" style={s.shareActionButton} onClick={(e) => { e.stopPropagation(); setShareActiveTarget(gallery); }}>
                        {gallery.ShareBannerActive ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
                        {gallery.ShareBannerActive ? t("roomGalleries.deactivate") : t("roomGalleries.activate")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreateGallery && (
        <CreateGalleryModal eventId={eventId} isARoom isPrivateARoom={isPrivateARoom} onClose={() => setShowCreateGallery(false)} onCreated={refreshGalleries} />
      )}

      {editGalleryTarget && (
        <EditNameModal
          title={t("roomGalleries.editModal.title")}
          label={t("roomGalleries.editModal.label")}
          value={editGalleryName}
          onChange={setEditGalleryName}
          onSave={handleEditGallery}
          onCancel={() => !actionLoading && setEditGalleryTarget(null)}
          loading={actionLoading}
          saveText={t("common:actions.save")}
        />
      )}

      {deleteGalleryTarget && (
        <ConfirmModal
          title={t("roomGalleries.deleteModal.title")}
          message={t("roomGalleries.deleteModal.message", { name: deleteGalleryTarget.Name })}
          confirmText={t("roomGalleries.deleteModal.confirmText")}
          danger
          loading={actionLoading}
          onConfirm={handleDeleteGallery}
          onCancel={() => !actionLoading && setDeleteGalleryTarget(null)}
        />
      )}

      {showBannerModal && (
        <SupportBannerModal eventId={eventId} onClose={() => { setShowBannerModal(false); refreshGalleries(); }} />
      )}

      {shareRankingTarget && (
        <ShareGalleryConsoleModal
          event={{ Id: eventId, StartDateTime: room?.StartDateTime, PromtorEventEndDateTime: room?.PromtorEventEndDateTime }}
          gallery={shareRankingTarget}
          onClose={() => setShareRankingTarget(null)}
        />
      )}

      {shareActiveTarget && (
        <ConfirmModal
          title={shareActiveTarget.ShareBannerActive ? t("roomGalleries.confirmDeactivateShare.title") : t("roomGalleries.confirmActivateShare.title")}
          message={shareActiveTarget.ShareBannerActive ? t("roomGalleries.confirmDeactivateShare.message") : t("roomGalleries.confirmActivateShare.message")}
          confirmText={shareActiveTarget.ShareBannerActive ? t("roomGalleries.deactivate") : t("roomGalleries.activate")}
          danger={shareActiveTarget.ShareBannerActive}
          loading={shareActiveLoading}
          onConfirm={confirmToggleShareActive}
          onCancel={() => !shareActiveLoading && setShareActiveTarget(null)}
        />
      )}
    </div>
  );
};

const s = {
  sectionHeader: { display: "flex", alignItems: "center", gap: 12, margin: "0 0 16px", justifyContent: "space-between" },
  sectionTitle: { margin: 0, fontSize: 17, fontWeight: 800, color: color.textPrimary, letterSpacing: -0.3 },
  sectionCount: { fontSize: 12.5, color: color.textSecondary, background: color.gray100, padding: "3px 11px", borderRadius: radius.pill, fontWeight: 500 },

  galleryGrid: { display: "flex", flexWrap: "wrap", gap: 16 },
  galleryCard: {
    background: color.surface, borderRadius: radius.md, overflow: "hidden",
    boxShadow: shadow.sm, border: `1px solid ${color.border}`, cursor: "pointer", width: 240,
  },
  colorBar: { height: 4 },
  galleryBody: { padding: "14px 16px 16px" },
  galleryTopRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, gap: 8 },
  galleryActions: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" },
  galleryName: {
    fontWeight: 700, fontSize: 14, color: color.textPrimary, flex: 1, minWidth: 0,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  galleryCounts: { display: "flex", gap: 14, fontSize: 12, color: color.textSecondary, marginBottom: 8 },
  countItem: { display: "inline-flex", alignItems: "center", gap: 4 },
  packageName: { display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: color.primary600, marginBottom: 8, fontWeight: 500 },
  galleryMeta: { display: "flex", flexWrap: "wrap", gap: 5, marginTop: 4 },
  cardActionBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    border: `1px solid ${color.borderStrong}`, background: color.surface, color: color.gray600,
    borderRadius: radius.sm, width: 24, height: 24, cursor: "pointer",
  },
  deleteActionBtn: { borderColor: color.danger50, color: color.danger500, background: color.danger50 },
  tag: { fontSize: 10, background: color.gray100, color: color.textSecondary, padding: "2px 8px", borderRadius: radius.pill, fontWeight: 500 },
  shareGalleryActions: { display: "flex", gap: 7, flexWrap: "wrap", marginTop: 13, paddingTop: 11, borderTop: `1px solid ${color.border}` },
  shareActionButton: { padding: "5px 8px", fontSize: 10.5, minHeight: 28 },
};

export default RoomGalleries;
